import { useMemo, ReactNode, FC } from 'react';
import { CssBaseline } from '@mui/material';
import {
    createTheme,
    ThemeOptions,
    ThemeProvider as MUIThemeProvider,
    StyledEngineProvider,
} from '@mui/material/styles';
import useSettings from '../hooks/useSettings';
import palette from './palette';
import typography, { FONT_TERTIARY } from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';
import Scrollbar from 'src/components/Scrollbar';
import useAuth from '../hooks/useAuth';

type ThemeProviderProps = {
    children: ReactNode;
};

const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
    const { themeMode, themeDirection } = useSettings();
    const { isClient, user } = useAuth();

    const isLight = themeMode === 'light';

    const isClientOrGuest = useMemo(() => !user || isClient, [isClient, user]);

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            palette: isLight ? (isClientOrGuest ? palette.warm : palette.light) : palette.dark,
            typography: {
                ...typography,
                ...(isClientOrGuest && {
                    h4: {
                        fontFamily: FONT_TERTIARY,
                        ...typography.h4,
                    },
                }),
                allVariants: {
                    color: isLight ? palette.light.text.primary : palette.dark.text.primary,
                },
            },
            breakpoints,
            shape: { borderRadius: 8 },
            direction: themeDirection,
            shadows: isLight ? shadows.light : shadows.dark,
            customShadows: isLight ? customShadows.light : customShadows.dark,
        }),
        [isClientOrGuest, isLight, themeDirection]
    );

    const theme = createTheme(themeOptions);

    theme.components = componentsOverride(theme);

    return (
        <StyledEngineProvider injectFirst>
            <MUIThemeProvider theme={theme}>
                <Scrollbar>
                    <CssBaseline />
                    {children}
                </Scrollbar>
            </MUIThemeProvider>
        </StyledEngineProvider>
    );
};

export default ThemeProvider;
